import {
  Box,
  Button,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Divider,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SettingsIcon from "@mui/icons-material/Settings";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { useEffect, useState } from "react";
import { useStore } from "../../app/stores/store";
import { observer } from "mobx-react-lite";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import FullPageLoader from "../../app/shared/common/FullPageLoader";
import MapLocationPicker from "../../app/shared/components/MapLocationPicker";

/* -------------------- Options -------------------- */
export type MyFloorprops = {
  id: string | number;
  name: string;
};

export const MyFloorData: MyFloorprops[] = [
  { id: 1, name: "Basement" },
  { id: 2, name: "Ground Floor" },
  { id: 3, name: "1st Floor" },
  { id: 4, name: "2nd Floor" },
  { id: 5, name: "3rd Floor or above" },
];

const AddOutlet = observer(() => {
  const { onboardingStore, toasterStore } = useStore();
  const { orgId, outletId } = useParams<{ orgId?: string; outletId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect whether we're in update mode based on the URL path
  const isUpdateMode = location.pathname.includes("update-outlet");
  const [prefillLoading, setPrefillLoading] = useState(isUpdateMode);

  // Initialize formik first
  const formik = useFormik({
    // validateOnChange: false,
    // validateOnBlur: true,
    // validateOnMount: false,
    initialValues: {
      // Basic Outlet Details
      outletName: "",
      outletManagerName: "",
      email: "",
      mobileNumber: "",
      alternateMobileNumber: "",
      panNumber: "",
      gstNumber: "",
      fssaiNumber: "",

      // Shipping Details
      shippingAddressLine1: "",
      shippingAddressLine2: "",
      shipTo: "",
      latitude: "",
      longitude: "",
      shippingLandmark: "",
      state: "",
      city: "",
      pincode: "",
      shippingMobileNumber: "",

      // Billing Details
      isBillingSameAsShipping: false,
      billingAddressLine1: "",
      billingAddressLine2: "",
      billTo: "",
      billingState: "",
      billingCity: "",
      billingPincode: "",
      billingMobileNumber: "",
      billingLandmark: "",

      // Additional Details
      openingTime: null as Date | null,
      closingTime: null as Date | null,
      lunchTime: null as Date | null,
      customerType: "",
      businessType: "",
      subBusinessType: "",
      storey: "",
      isLiftAvailable: false,
      cuisineType: "",
    },
    validationSchema: Yup.object({
      // Basic Outlet Details
      outletName: Yup.string().required("Outlet Name is required"),
      mobileNumber: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
        .required("Mobile Number is required"),
      email: Yup.string().email("Invalid email format"),
      alternateMobileNumber: Yup.string().matches(
        /^[0-9]{10}$/,
        "Alternate mobile must be 10 digits",
      ),
      panNumber: Yup.string()
        .trim()
        .transform((value) => value?.toUpperCase())
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "Invalid PAN format"),
      gstNumber: Yup.string()
        .trim()
        .transform((value) => value?.toUpperCase())
        .matches(
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/,
          "Invalid GST format",
        ),
      fssaiNumber: Yup.string()
        .trim()
        .transform((value) => value?.toUpperCase())
        .matches(/^[0-9]{14}$/, "FSSAI must be 14 digits"),

      // Shipping Details
      shippingAddressLine1: Yup.string().required("Address Line 1 is required"),
      latitude: Yup.string().required("Latitude is required"),
      longitude: Yup.string().required("Longitude is required"),
      state: Yup.string().required("State is required"),
      city: Yup.string().required("City is required"),
      pincode: Yup.string()
        .matches(/^[1-9][0-9]{5}$/, "Pincode must be 6 digits")
        .required("Pincode is required"),
      shippingMobileNumber: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
        .required("Shipping Mobile Number is required"),

      // Billing Details
      billingAddressLine1: Yup.string().required(
        "Billing Address Line 1 is required",
      ),
      billingState: Yup.string().required("State is required"),
      billingCity: Yup.string().required("City is required"),
      billingPincode: Yup.string()
        .matches(/^[1-9][0-9]{5}$/, "Pincode must be 6 digits")
        .required("Billing Pincode is required"),
      billingMobileNumber: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
        .required("Billing Mobile Number is required"),

      // Additional Details
      customerType: Yup.string().required("Customer Type is required"),
      // businessType and subBusinessType are conditionally required via `validate` below
    }),
    // Conditional validation: businessType/subBusinessType only required for DIT customer types
    validate: (values) => {
      const errors: Partial<Record<string, string>> = {};
      const selectedCustomerType = onboardingStore.customerTypeList?.data.find(
        (ct) => String(ct.id) === String(values.customerType),
      );
      const isDIT = selectedCustomerType?.identityCode === "DIT";

      if (isDIT) {
        // businessType is required for DIT and when business types are available
        if (!values.businessType && (onboardingStore.businessTypeList?.data?.length ?? 0) > 0) {
          errors.businessType = "Business Type is required";
        }
        // subBusinessType is required for DIT only when sub-types exist for the chosen business type
        if (!values.subBusinessType && (onboardingStore.subBusinessTypeList?.data?.length ?? 0) > 0) {
          errors.subBusinessType = "Sub Business Type is required";
        }
      }

      return errors;
    },
    onSubmit: async (values) => {
      const stateObj = onboardingStore.stateList?.data.find(
        (s) => s.name === values.state,
      );
      const cityObj = onboardingStore.cityList?.data.find(
        (c) => c.name === values.city,
      );
      const billingStateObj = onboardingStore.stateList?.data.find(
        (s) => s.name === values.billingState,
      );
      const billingCityObj = onboardingStore.cityList?.data.find(
        (c) => c.name === values.billingCity,
      );

      const userStr = localStorage.getItem("user");
      const userData = userStr ? JSON.parse(userStr) : null;
      const outletSupportManagerId =
        userData?.data?.admin?.id || userData?.data?.id || undefined;

      const payload = {
        adminFlag: true,
        isVerified: true,
        customerTypeId: values.customerType
          ? Number(values.customerType)
          : undefined,
        customerOrgId: orgId ? Number(orgId) : undefined,
        name: values.outletName,
        email: values.email || undefined,
        mobile: values.mobileNumber,
        secondaryMobile: values.alternateMobileNumber || undefined,
        panNumber: values.panNumber || undefined,
        gstNumber: values.gstNumber || undefined,
        fssaiNumber: values.fssaiNumber || undefined,
        outletSupportManagerId: outletSupportManagerId,
        shippingStateId: stateObj?.id,
        shippingCityId: cityObj?.id,
        shippingAddressLine1: values.shippingAddressLine1,
        shippingAddressLine2: values.shippingAddressLine2,
        shippingPincode: values.pincode,
        shippingLat: values.latitude,
        shippingLong: values.longitude,
        billingStateId: billingStateObj?.id,
        billingCityId: billingCityObj?.id,
        billingAddressLine1: values.billingAddressLine1,
        billingAddressLine2: values.billingAddressLine2,
        billingPincode: values.billingPincode,
        openingTime: values.openingTime
          ? new Date(values.openingTime).toLocaleTimeString("en-GB")
          : undefined,
        closingTime: values.closingTime
          ? new Date(values.closingTime).toLocaleTimeString("en-GB")
          : undefined,
        lunchTime: values.lunchTime
          ? new Date(values.lunchTime).toLocaleTimeString("en-GB")
          : undefined,
        isSignupCashbackCredit: false,
        storey: values.storey ? Number(values.storey) : undefined,
        isLiftAvailable: values.isLiftAvailable,
        billingAddressContactNumber: values.billingMobileNumber,
        shippingAddressContactNumber: values.shippingMobileNumber,
        outletManager: values.outletManagerName || undefined,
        cuisineType: values.cuisineType || null,
        shippingAttention: values.shipTo || undefined,
        billingAttention: values.billTo || undefined,
        billingLandMark: values.billingLandmark || "",
        shippingLandMark: values.shippingLandmark || "",
        addressType: values.businessType || undefined,
        addressSubType: values.subBusinessType || undefined,
      };

      try {
        if (isUpdateMode && outletId) {
          // PATCH update
          const response = await onboardingStore.updateOutlet(Number(outletId), payload);
          if (response && response.status_code === 1) {
            toasterStore.success("Outlet updated successfully");
            navigate(`/outlet/upload-attachment/${outletId}`);
          }
        } else {
          // POST create
          const response = await onboardingStore.addOutlet(payload);
          if (response && response.status_code === 1) {
            const newOutletId = response.data.id;
            formik.resetForm();
            toasterStore.success("Outlet added successfully");
            navigate(`/outlet/upload-attachment/${newOutletId}`);
          }
        }
      } catch (error: any) {
        const msg =
          error?.response?.data?.msg ||
          "Something went wrong. Please try again.";
        toasterStore.error(msg);
      }
    },
  });

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        onboardingStore.getAllState(),
        onboardingStore.getCustomerTypes(),
        onboardingStore.getCrmOnboardCustomers(),
      ]);
    };
    init();
  }, [onboardingStore]);

  // Fetch business types when customer type changes
  useEffect(() => {
    if (!formik.values.customerType) {
      onboardingStore.clearBusinessTypes();
      return;
    }

    const getBusinessTypesForCustomer = async () => {
      try {
        const selectedCustomerType = onboardingStore.customerTypeList?.data.find(
          (ct) => String(ct.id) === String(formik.values.customerType),
        );
        
        // Only fetch business types if identityCode is DIT
        if (selectedCustomerType?.identityCode !== "DIT") {
          onboardingStore.clearBusinessTypes();
          formik.setFieldValue("businessType", "");
          formik.setFieldValue("subBusinessType", "");
          return;
        }
        
        if (selectedCustomerType?.name === "General Trade") {
          // For General Trade, fetch addresstypegt
          await onboardingStore.getBusinessTypes("addresstypegt");
        } else {
          // For other types, fetch addresstypeb2b
          await onboardingStore.getBusinessTypes("addresstypeb2b");
        }
        
        // Reset business type and sub business type when customer type changes
        formik.setFieldValue("businessType", "");
        formik.setFieldValue("subBusinessType", "");
      } catch (error) {
        console.error("Error fetching business types:", error);
      }
    };

    getBusinessTypesForCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.customerType, onboardingStore.customerTypeList]);

  // Fetch sub-business types when business type changes
  useEffect(() => {
    if (!formik.values.businessType || !formik.values.customerType) {
      onboardingStore.clearSubBusinessTypes();
      return;
    }

    const getSubBusinessTypes = async () => {
      try {
        await onboardingStore.getSubBusinessTypes(formik.values.businessType);
        // Reset sub business type when business type changes
        formik.setFieldValue("subBusinessType", "");
      } catch (error) {
        console.error("Error fetching sub business types:", error);
      }
    };

    getSubBusinessTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.businessType]);

  // Prefill form when in update mode — calls getOutletByOrgId API directly
  useEffect(() => {
    if (!isUpdateMode || !outletId || !orgId) return;
    if (
      !onboardingStore.stateList ||
      !onboardingStore.customerTypeList
    )
      return;

    const prefill = async () => {
      setPrefillLoading(true);
      try {
        // Fetch outlets for this org directly from the API
        const res = await onboardingStore.getOutletByOrgId(Number(orgId));
        if (!res || res.status_code !== 1 || !res.data?.length) {
          setPrefillLoading(false);
          return;
        }

        // Find the specific outlet matching the outletId in the URL
        const outlet = res.data.find((o) => String(o.id) === String(outletId));
        if (!outlet) {
          setPrefillLoading(false);
          return;
        }

        // Load cities for the outlet's shipping state
        if (outlet.shippingStateId) {
          await onboardingStore.getAllCities(Number(outlet.shippingStateId));
        }

        const parseTime = (t: string | null) => {
          if (!t) return null;
          const [h, m] = t.split(":");
          const d = new Date();
          d.setHours(Number(h), Number(m), 0);
          return d;
        };

        const mobileNum = outlet.mobile || "";
        // If shipping mobile is not provided, use the main mobile number
        const shippingMobileNum = outlet.shippingAddressContactNumber || mobileNum;
        
        const matchedCrmCustomer = onboardingStore.crmOnboardCustomers?.find(
          (c: any) => {
            const cPhone = c.contact?.phone ? String(c.contact.phone).trim() : "";
            const oPhone = outlet.mobile ? String(outlet.mobile).trim() : "";
            const cGst = c.customer?.gst ? String(c.customer.gst).trim().toUpperCase() : "";
            const oGst = outlet.gstNumber ? String(outlet.gstNumber).trim().toUpperCase() : "";
            const cEmail = c.contact?.email ? String(c.contact.email).trim().toLowerCase() : "";
            const oEmail = outlet.email ? String(outlet.email).trim().toLowerCase() : "";

            const phoneMatch = cPhone && oPhone && (cPhone.includes(oPhone) || oPhone.includes(cPhone));
            const gstMatch = cGst && oGst && cGst === oGst;
            const emailMatch = cEmail && oEmail && cEmail === oEmail;
            
            return phoneMatch || gstMatch || emailMatch;
          }
        );


        const stateCustomerTypeId = location.state?.customerTypeId;

        const customerTypeVal = outlet.customerTypeId 
          ? String(outlet.customerTypeId) 
          : (stateCustomerTypeId 
              ? String(stateCustomerTypeId) 
              : (matchedCrmCustomer?.customer?.customerTypeId ? String(matchedCrmCustomer.customer.customerTypeId) : ""));

        
        formik.setValues({
          outletName: outlet.name || "",
          outletManagerName: outlet.outletManager || "",
          email: outlet.email || "",
          mobileNumber: mobileNum,
          alternateMobileNumber: outlet.secondaryMobile || "",
          panNumber: outlet.panNumber || "",
          gstNumber: outlet.gstNumber || "",
          fssaiNumber: outlet.fssaiNumber || "",
          shippingAddressLine1: outlet.shippingAddressLine1 || "",
          shippingAddressLine2: outlet.shippingAddressLine2 || "",
          shipTo: outlet.shippingAttention || "",
          latitude: outlet.shippingLat || "",
          longitude: outlet.shippingLong || "",
          shippingLandmark: outlet.shippingLandMark || "",
          state: outlet.shippingState?.name || "",
          city: outlet.shippingCity?.name || "",
          pincode: outlet.shippingPincode || "",
          shippingMobileNumber: shippingMobileNum,
          isBillingSameAsShipping: outlet.isBillingAddressSameAsShippingAddress || false,
          billingAddressLine1: outlet.billingAddressLine1 || "",
          billingAddressLine2: outlet.billingAddressLine2 || "",
          billTo: outlet.billingAttention || "",
          billingState: outlet.shippingState?.name || "",
          billingCity: outlet.shippingCity?.name || "",
          billingPincode: outlet.billingPincode || "",
          billingMobileNumber: outlet.billingAddressContactNumber || "",
          billingLandmark: outlet.billingLandMark || "",
          openingTime: parseTime(outlet.openingTime),
          closingTime: parseTime(outlet.closingTime),
          lunchTime: parseTime(outlet.lunchTime),
          customerType: customerTypeVal,
          businessType: outlet.businessType || "",
          subBusinessType: outlet.subBusinessType || "",
          storey: outlet.storey ? String(outlet.storey) : "",
          isLiftAvailable: outlet.isLiftAvailable || false,
          cuisineType: outlet.cuisineType || "",
        });
      } catch (e) {
        console.error("Error prefilling outlet form:", e);
      } finally {
        setPrefillLoading(false);
      }
    };
    prefill();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isUpdateMode,
    outletId,
    orgId,
    onboardingStore.stateList,
    onboardingStore.customerTypeList,
    onboardingStore.crmOnboardCustomers,
  ]);

  // Helper functions
  const fieldError = (name: keyof typeof formik.values) =>
    Boolean(formik.touched[name] && formik.errors[name]);
  const fieldHelper = (name: keyof typeof formik.values) =>
    formik.touched[name] ? (formik.errors[name] as string) : "";

  // Get cities based on selected state from store
  const getShippingCities = () => {
    return onboardingStore.cityList?.data || [];
  };

  const getBillingCities = () => {
    return onboardingStore.cityList?.data || [];
  };

  // Helper to render single‑line fields
  const renderFields = (
    fields: {
      name: keyof typeof formik.values;
      label: string;
      required?: boolean;
      type?: string;
      onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    }[],
  ) =>
    fields.map((field) => {
      const fieldProps = formik.getFieldProps(field.name);
      return (
      <Box
        key={field.name}
        sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
      >
        <TextField
          fullWidth
          label={field.label}
          type={field.type || "text"}
          {...fieldProps}
          onChange={field.onChange || fieldProps.onChange}
          error={fieldError(field.name)}
          helperText={fieldHelper(field.name)}
          required={field.required}
        />
      </Box>
    )});

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <FullPageLoader
          open={prefillLoading || onboardingStore.isAddingOutlet}
          message={
            prefillLoading
              ? "Loading outlet details..."
              : isUpdateMode
              ? "Updating outlet..."
              : "Creating outlet..."
          }
        />
        <Box>
          {/* Header */}
          <Paper
            elevation={0}
            sx={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 3,
              mb: 3,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BusinessIcon />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight={600}>
                  {isUpdateMode ? "Edit Outlet" : "Add Outlet"}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
            {/* Basic Outlet Details */}
            <Paper
              elevation={0}
              sx={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                borderRadius: 3,
                p: 2,
                mb: 3,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
              >
                <PersonIcon />
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  Basic Outlet Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {renderFields([
                  { name: "outletName", label: "Outlet Name", required: true },
                  { name: "outletManagerName", label: "Outlet Manager Name" },
                  { name: "email", label: "Email", type: "email" },
                  {
                    name: "mobileNumber",
                    label: "Mobile Number",
                    required: true,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      formik.handleChange(e);
                      // Auto-fill shipping mobile number if it hasn't been manually edited
                      if (!formik.touched.shippingMobileNumber) {
                        formik.setFieldValue("shippingMobileNumber", e.target.value);
                      }
                    },
                  },
                  {
                    name: "alternateMobileNumber",
                    label: "Alternate Mobile Number",
                  },
                  { name: "panNumber", label: "PAN Number" },
                  { name: "gstNumber", label: "GST Number" },
                  { name: "fssaiNumber", label: "FSSAI Number" },
                ])}
              </Box>
            </Paper>

            {/* Shipping Details */}
            <Paper
              elevation={0}
              sx={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                borderRadius: 3,
                p: 2,
                mb: 3,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
              >
                <LocalShippingIcon />
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  Shipping Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {renderFields([
                  { name: "shipTo", label: "Ship To" },
                ])}
                {/* Address Line 1 & 2 */}
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TextField
                    fullWidth
                    label="Shipping Address Line 1"
                    multiline
                    rows={4}
                    {...formik.getFieldProps("shippingAddressLine1")}
                    inputProps={{ maxLength: 40 }}
                    error={fieldError("shippingAddressLine1")}
                    helperText={
                      fieldHelper("shippingAddressLine1") ||
                      `${formik.values.shippingAddressLine1.length}/40 characters (${40 - formik.values.shippingAddressLine1.length} remaining)`
                    }
                    required
                  />
                </Box>
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TextField
                    fullWidth
                    label="Shipping Address Line 2"
                    multiline
                    rows={4}
                    {...formik.getFieldProps("shippingAddressLine2")}
                    inputProps={{ maxLength: 60 }}
                    error={fieldError("shippingAddressLine2")}
                    helperText={
                      fieldHelper("shippingAddressLine2") ||
                      `${formik.values.shippingAddressLine2.length}/60 characters (${60 - formik.values.shippingAddressLine2.length} remaining)`
                    }
                  />
                </Box>

                <Box sx={{ flex: "1 1 100%" }}>
                  <MapLocationPicker
                    latitude={formik.values.latitude}
                    longitude={formik.values.longitude}
                    onLatitudeChange={(value) =>
                      formik.setFieldValue("latitude", value)
                    }
                    onLongitudeChange={(value) =>
                      formik.setFieldValue("longitude", value)
                    }
                    label="Select Shipping Location"
                  />
                </Box>

                {renderFields([
                  {
                    name: "shippingLandmark",
                    label: "Shipping Landmark",
                  },
                ])}

                {/* State Select */}
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TextField
                    select
                    fullWidth
                    label="State"
                    value={formik.values.state}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue("state", value);
                      formik.setFieldValue("city", "");
                      const stateObj = onboardingStore.stateList?.data.find(
                        (s) => s.name === value,
                      );
                      if (stateObj) {
                        onboardingStore.getAllCities(stateObj.id);
                      }
                    }}
                    error={fieldError("state")}
                    helperText={fieldHelper("state")}
                    required
                  >
                    <MenuItem value="">Select State</MenuItem>
                    {onboardingStore.stateList?.data?.map((state) => (
                      <MenuItem key={state.id} value={state.name}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* City Select */}
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TextField
                    select
                    fullWidth
                    label="City"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    name="city"
                    error={fieldError("city")}
                    helperText={fieldHelper("city")}
                    required
                    disabled={!formik.values.state}
                    SelectProps={{
                      onOpen: () => {
                        const stateObj = onboardingStore.stateList?.data.find(
                          (s) => s.name === formik.values.state,
                        );
                        if (stateObj) {
                          onboardingStore.getAllCities(stateObj.id);
                        }
                      },
                    }}
                  >
                    <MenuItem value="">Select City</MenuItem>
                    {getShippingCities().map((city) => (
                      <MenuItem key={city.id} value={city.name}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {renderFields([
                  { name: "pincode", label: "Pincode", required: true },
                  {
                    name: "shippingMobileNumber",
                    label: "Shipping Mobile Number",
                    required: true,
                  },
                ])}
              </Box>
            </Paper>

            {/* Billing Details */}
            <Paper
              elevation={0}
              sx={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                borderRadius: 3,
                p: 2,
                mb: 3,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
              >
                <ReceiptIcon />
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  Billing Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.isBillingSameAsShipping}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (checked) {
                        formik.setValues({
                          ...formik.values,
                          isBillingSameAsShipping: true,
                          billingAddressLine1: formik.values.shippingAddressLine1,
                          billingAddressLine2: formik.values.shippingAddressLine2,
                          billTo: formik.values.shipTo,
                          billingState: formik.values.state,
                          billingCity: formik.values.city,
                          billingPincode: formik.values.pincode,
                          billingMobileNumber: formik.values.shippingMobileNumber,
                          billingLandmark: formik.values.shippingLandmark,
                        });
                      } else {
                        formik.setFieldValue("isBillingSameAsShipping", false);
                      }
                    }}
                  />
                }
                label="Billing Address is Same As Shipping Address"
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {renderFields([{ name: "billTo", label: "Bill To" }])}
                {/* Billing Address Lines */}
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TextField
                    fullWidth
                    label="Billing Address Line 1"
                    multiline
                    rows={4}
                    {...formik.getFieldProps("billingAddressLine1")}
                    inputProps={{ maxLength: 40 }}
                    error={fieldError("billingAddressLine1")}
                    helperText={
                      fieldHelper("billingAddressLine1") ||
                      `${formik.values.billingAddressLine1.length}/40 characters (${40 - formik.values.billingAddressLine1.length} remaining)`
                    }
                    required
                  />
                </Box>
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TextField
                    fullWidth
                    label="Billing Address Line 2"
                    multiline
                    rows={4}
                    {...formik.getFieldProps("billingAddressLine2")}
                    inputProps={{ maxLength: 60 }}
                    error={fieldError("billingAddressLine2")}
                    helperText={
                      fieldHelper("billingAddressLine2") ||
                      `${formik.values.billingAddressLine2.length}/60 characters (${60 - formik.values.billingAddressLine2.length} remaining)`
                    }
                  />
                </Box>

                {/* Billing State */}
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TextField
                    select
                    fullWidth
                    label="State"
                    value={formik.values.billingState}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue("billingState", value);
                      formik.setFieldValue("billingCity", "");
                      const stateObj = onboardingStore.stateList?.data.find(
                        (s) => s.name === value,
                      );
                      if (stateObj) {
                        onboardingStore.getAllCities(stateObj.id);
                      }
                    }}
                    name="billingState"
                    error={fieldError("billingState")}
                    helperText={fieldHelper("billingState")}
                    required
                  >
                    <MenuItem value="">Select State</MenuItem>
                    {onboardingStore.stateList?.data?.map((state) => (
                      <MenuItem key={state.id} value={state.name}>
                        {state.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Billing City */}
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TextField
                    select
                    fullWidth
                    label="City"
                    value={formik.values.billingCity}
                    onChange={formik.handleChange}
                    name="billingCity"
                    error={fieldError("billingCity")}
                    helperText={fieldHelper("billingCity")}
                    required
                    disabled={!formik.values.billingState}
                    SelectProps={{
                      onOpen: () => {
                        const stateObj = onboardingStore.stateList?.data.find(
                          (s) => s.name === formik.values.billingState,
                        );
                        if (stateObj) {
                          onboardingStore.getAllCities(stateObj.id);
                        }
                      },
                    }}
                  >
                    <MenuItem value="">Select City</MenuItem>
                    {getBillingCities().map((city) => (
                      <MenuItem key={city.id} value={city.name}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {renderFields([
                  { name: "billingPincode", label: "Pincode", required: true },
                  {
                    name: "billingMobileNumber",
                    label: "Billing Mobile Number",
                    required: true,
                  },
                  {
                    name: "billingLandmark",
                    label: "Billing Landmark",
                  },
                ])}
              </Box>
            </Paper>

            {/* Additional Details */}
            <Paper
              elevation={0}
              sx={{
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(10px)",
                borderRadius: 3,
                p: 2,
                mb: 3,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
              >
                <SettingsIcon />
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  Additional Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                {/* Time Pickers */}
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TimePicker
                    label="Opening Time"
                    value={formik.values.openingTime}
                    onChange={(newValue) =>
                      formik.setFieldValue("openingTime", newValue)
                    }
                    minutesStep={15}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: fieldError("openingTime"),
                        helperText: fieldHelper("openingTime"),
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TimePicker
                    label="Closing Time"
                    value={formik.values.closingTime}
                    onChange={(newValue) =>
                      formik.setFieldValue("closingTime", newValue)
                    }
                    minutesStep={15}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: fieldError("closingTime"),
                        helperText: fieldHelper("closingTime"),
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TimePicker
                    label="Lunch Time"
                    value={formik.values.lunchTime}
                    onChange={(newValue) =>
                      formik.setFieldValue("lunchTime", newValue)
                    }
                    minutesStep={15}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: fieldError("lunchTime"),
                        helperText: fieldHelper("lunchTime"),
                      },
                    }}
                  />
                </Box>

                {/* Customer Type */}
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TextField
                    select
                    fullWidth
                    label="Customer Type"
                    value={formik.values.customerType}
                    onChange={formik.handleChange}
                    name="customerType"
                    error={fieldError("customerType")}
                    helperText={fieldHelper("customerType")}
                    required
                    InputLabelProps={{ required: true }}
                  >
                    <MenuItem value="">Select Customer Type</MenuItem>
                    {onboardingStore.customerTypeList?.data?.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Business Type */}
                {onboardingStore.businessTypeList &&
                  onboardingStore.businessTypeList.data.length > 0 && (
                    <Box
                      sx={{
                        flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                      }}
                    >
                      <TextField
                        select
                        fullWidth
                        label="Business Type"
                        value={formik.values.businessType}
                        onChange={(e) => {
                          formik.setFieldValue("businessType", e.target.value);
                        }}
                        name="businessType"
                        error={fieldError("businessType")}
                        helperText={fieldHelper("businessType")}
                        disabled={onboardingStore.loadingBusinessTypes}
                        required
                        InputLabelProps={{ required: true }}
                      >
                        <MenuItem value="">Select Business Type</MenuItem>
                        {onboardingStore.businessTypeList.data.map((bt) => (
                          <MenuItem key={bt.id} value={bt.value}>
                            {bt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  )}

                {/* Sub Business Type */}
                {onboardingStore.subBusinessTypeList &&
                  onboardingStore.subBusinessTypeList.data.length > 0 && (
                    <Box
                      sx={{
                        flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" },
                      }}
                    >
                      <TextField
                        select
                        fullWidth
                        label="Sub Business Type"
                        value={formik.values.subBusinessType}
                        onChange={(e) => {
                          formik.setFieldValue("subBusinessType", e.target.value);
                        }}
                        name="subBusinessType"
                        error={fieldError("subBusinessType")}
                        helperText={fieldHelper("subBusinessType")}
                        disabled={!formik.values.businessType || onboardingStore.loadingBusinessTypes}
                        required
                        InputLabelProps={{ required: true }}
                      >
                        <MenuItem value="">Select Sub Business Type</MenuItem>
                        {onboardingStore.subBusinessTypeList.data.map((sbt) => (
                          <MenuItem key={sbt.id} value={sbt.value}>
                            {sbt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  )}

                {/* Storey */}
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TextField
                    select
                    fullWidth
                    label="Floor"
                    value={formik.values.storey}
                    onChange={formik.handleChange}
                    name="storey"
                    error={fieldError("storey")}
                    helperText={fieldHelper("storey")}
                  >
                    <MenuItem value="">Select Floor</MenuItem>
                    {MyFloorData.map((storey) => (
                      <MenuItem key={storey.id} value={storey.id}>
                        {storey.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Lift Available Checkbox */}
                <Box
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.isLiftAvailable}
                        onChange={(e) =>
                          formik.setFieldValue(
                            "isLiftAvailable",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Is Lift Available?"
                  />
                </Box>

                {/* Cuisine Type */}
                <Box sx={{ flex: "1 1 100%" }}>
                  <TextField
                    fullWidth
                    label="Cuisine Type"
                    multiline
                    rows={3}
                    {...formik.getFieldProps("cuisineType")}
                    error={fieldError("cuisineType")}
                    helperText={fieldHelper("cuisineType")}
                    placeholder="e.g., North Indian, Chinese, Continental, etc."
                  />
                </Box>
              </Box>
            </Paper>

            {/* Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mb: 3,
              }}
            >
              <Button type="reset" variant="outlined" size="large">
                Reset
              </Button>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={
                  onboardingStore.isAddingOutlet ||
                  prefillLoading
                }
              >
                {onboardingStore.isAddingOutlet
                  ? isUpdateMode ? "Updating..." : "Creating..."
                  : isUpdateMode ? "Update Outlet" : "Create Outlet"}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </LocalizationProvider>
  );
});

export default AddOutlet;
